import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, Package, CreditCard, Truck, Home, X, RotateCcw, Search } from "lucide-react";
import api, { eur } from "@/lib/api";

export const STATUS_LABELS = {
  received: "Παραλήφθηκε", payment_confirmed: "Πληρωμή Επιβεβαιώθηκε", pending_payment: "Αναμονή Πληρωμής",
  preparing: "Προετοιμασία", shipped: "Απεστάλη", delivered: "Παραδόθηκε", cancelled: "Ακυρώθηκε", refunded: "Επιστροφή Χρημάτων",
};

export function StatusBadge({ status }) {
  const map = {
    received: "bg-blue-500/15 text-blue-400", payment_confirmed: "bg-green-500/15 text-green-400",
    pending_payment: "bg-yellow-500/15 text-yellow-400", preparing: "bg-purple-500/15 text-purple-400",
    shipped: "bg-cyan-500/15 text-cyan-400", delivered: "bg-green-500/15 text-green-400",
    cancelled: "bg-red-500/15 text-red-400", refunded: "bg-zinc-500/15 text-zinc-400",
  };
  return <span className={`px-2.5 py-1 rounded-full text-[11px] font-mono font-bold uppercase ${map[status] || "bg-zinc-700"}`}>{STATUS_LABELS[status] || status}</span>;
}

const FLOW = ["received", "payment_confirmed", "preparing", "shipped", "delivered"];
const ICONS = { received: Check, payment_confirmed: CreditCard, preparing: Package, shipped: Truck, delivered: Home };

export default function OrderTracking() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [q, setQ] = useState(orderId || "");

  const load = async (num) => {
    try { const { data } = await api.get(`/orders/track/${num}`); setOrder(data); setNotFound(false); }
    catch { setNotFound(true); setOrder(null); }
  };
  useEffect(() => { if (orderId) load(orderId); }, [orderId]);

  const activeIdx = order ? FLOW.indexOf(order.status === "payment_confirmed" && order.payment_method === "cod" ? "received" : order.status) : -1;
  const cancelled = order && (order.status === "cancelled" || order.status === "refunded");

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#FF2E4C] mb-1">ΠΑΡΑΚΟΛΟΥΘΗΣΗ</p>
      <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tighter mb-6">Order Tracking</h1>

      <div className="flex gap-2 mb-8">
        <div className="flex-1 flex items-center gap-2 bg-[#0A0A0B] border border-zinc-700 rounded px-3">
          <Search className="w-4 h-4 text-zinc-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Αριθμός παραγγελίας π.χ. SKG..." className="flex-1 bg-transparent py-3 text-sm outline-none uppercase" data-testid="track-input" />
        </div>
        <button onClick={() => load(q.trim())} className="btn-red px-6 text-xs font-bold uppercase rounded" data-testid="track-search-btn">ΑΝΑΖΗΤΗΣΗ</button>
      </div>

      {notFound && <p className="text-zinc-400">Η παραγγελία δεν βρέθηκε. Έλεγξε τον αριθμό.</p>}

      {order && (
        <div className="space-y-8">
          <div className="card-surface rounded-lg p-5 flex flex-wrap items-center justify-between gap-3">
            <div><p className="font-display font-bold uppercase text-lg">#{order.order_number}</p><p className="text-xs text-zinc-500 font-mono">{new Date(order.created_at).toLocaleDateString("el-GR")}</p></div>
            <StatusBadge status={order.status} />
          </div>

          {cancelled ? (
            <div className="card-surface rounded-lg p-6 flex items-center gap-3">
              {order.status === "refunded" ? <RotateCcw className="w-6 h-6 text-zinc-400" /> : <X className="w-6 h-6 text-red-400" />}
              <p className="text-zinc-300">{order.status === "refunded" ? "Έγινε επιστροφή χρημάτων για αυτή την παραγγελία." : "Αυτή η παραγγελία ακυρώθηκε."}</p>
            </div>
          ) : (
            <div className="relative flex justify-between">
              <div className="absolute top-5 left-0 right-0 h-0.5 bg-zinc-800" />
              <div className="absolute top-5 left-0 h-0.5 bg-[#FF2E4C] transition-all duration-700" style={{ width: `${Math.max(0, (activeIdx / (FLOW.length - 1)) * 100)}%` }} />
              {FLOW.map((s, i) => {
                const Icon = ICONS[s];
                const active = i <= activeIdx;
                return (
                  <div key={s} className="relative z-10 flex flex-col items-center gap-2 flex-1">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${active ? "bg-[#FF2E4C] border-[#FF2E4C]" : "bg-[#18181C] border-zinc-700"}`}><Icon className={`w-4 h-4 ${active ? "text-white" : "text-zinc-600"}`} /></div>
                    <span className={`text-[9px] sm:text-[10px] font-mono uppercase text-center leading-tight ${active ? "text-white" : "text-zinc-600"}`}>{STATUS_LABELS[s]}</span>
                  </div>
                );
              })}
            </div>
          )}

          <div className="card-surface rounded-lg p-5">
            <h3 className="font-display font-bold uppercase text-sm mb-4">Προϊόντα</h3>
            <div className="space-y-3">
              {order.items.map((it, i) => (
                <div key={i} className="flex gap-3 items-center">
                  <img src={it.image} alt="" className="w-14 h-16 object-cover rounded" />
                  <div className="flex-1"><p className="text-sm font-semibold">{it.name}</p><p className="text-xs font-mono text-zinc-500 uppercase">{it.color}/{it.size} · x{it.quantity}</p></div>
                  <span className="text-sm font-semibold">{eur(it.price * it.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-zinc-800 mt-4 pt-4 space-y-1.5 text-sm">
              <div className="flex justify-between text-zinc-400"><span>Υποσύνολο</span><span>{eur(order.subtotal)}</span></div>
              {order.discount > 0 && <div className="flex justify-between text-[#FF2E4C]"><span>Έκπτωση</span><span>-{eur(order.discount)}</span></div>}
              <div className="flex justify-between text-zinc-400"><span>Μεταφορικά</span><span>{order.shipping_cost === 0 ? "ΔΩΡΕΑΝ" : eur(order.shipping_cost)}</span></div>
              <div className="flex justify-between font-display font-black pt-2"><span>ΣΥΝΟΛΟ</span><span>{eur(order.total)}</span></div>
            </div>
          </div>

          <div className="card-surface rounded-lg p-5 text-sm">
            <h3 className="font-display font-bold uppercase text-sm mb-3">Αποστολή σε</h3>
            <p className="text-zinc-300">{order.customer.first_name} {order.customer.last_name}</p>
            <p className="text-zinc-400">{order.customer.address}, {order.customer.city} {order.customer.postal_code}</p>
            <p className="text-zinc-400">{order.customer.phone} · {order.shipping_method}</p>
          </div>
        </div>
      )}
      <Link to="/shop" className="inline-block mt-8 text-xs font-mono uppercase text-zinc-400 hover:text-white">← Συνέχεια αγορών</Link>
    </div>
  );
}
